import React, { useEffect, useState } from "react";
import { compose, withApollo } from "react-apollo";
import { connect } from "react-redux";
import { OwcDrawer, OwcIcon, OwcIconButton, OwcList, OwcTypography, OwcExpandable } from "@one/react";
import { getEnv } from "../utils/helpers/text";
import { OwcListItem } from "@one/react/dist";
import { useHistory } from "react-router";
import styles from "./AppDrawer.module.scss";
import { get, snakeCase } from "lodash";
import { generateID, THEME } from "@digitallab/grid-common-components";
import { ADMIN_SUB_MENU, MY_LAB_MENU_ITEM, HOME_SUB_MENU, Module } from "../constants";
import { checkRoleIsLaningPageAdmin } from "../utils/helpers/checkRoleIsLaningPageAdmin";
import useAuthenticatedUser from "../utils/hooks/useAuthenticatedUser";
import { getCustomGroups } from "../features/landingPageUser/getCustomGroup";

const currentEnv = getEnv();

const AppDrawer = ({ children }) => {
  const user = useAuthenticatedUser();
  const userCustomGroups = getCustomGroups(get(user, "signInUserSession.idToken.payload.custom:groups") || []);
  const predUserGroup = `GLODIADigitalLab${currentEnv?.toUpperCase() ?? ""}_pRED_User`;
  const isPredUser = userCustomGroups.includes(predUserGroup);
  const [sideMenu, setSideMenu] = useState(HOME_SUB_MENU);
  const history = useHistory();
  const [isMini, setIsMini] = useState(false);
  const topBannerHt = 48;
  const envHeaderHt = 46.39;
  const mainHeaderHt = 27.8; // main menu + footer

  const calcHeight = () => {
    return window.innerHeight - mainHeaderHt - (currentEnv && envHeaderHt) - topBannerHt;
  };

  useEffect(() => {
    async function getSideMenuBasedOnRole() {
      const isAdminUser = await checkRoleIsLaningPageAdmin();
      const menu = [...HOME_SUB_MENU];
      if (!isPredUser) {
        menu.unshift(MY_LAB_MENU_ITEM);
      }

      if (isAdminUser) {
        menu.push(...ADMIN_SUB_MENU);
      }
      setSideMenu(menu);
    }
    getSideMenuBasedOnRole();
  }, [isPredUser]);

  const containerHeightLoad = calcHeight();

  const [containerHeight, setContainerHeight] = useState(containerHeightLoad);

  useEffect(() => {
    const height = calcHeight();
    setContainerHeight(height);
    // eslint-disable-next-line
  }, []);

  window.addEventListener("resize", function () {
    setContainerHeight(calcHeight());
  });

  const handleRedirect = (item) => {
    if (!item.isDisabled && item.url !== null) {
      history.replace(item.url);
    }
  };
  const [expandedSubMenus, setExpandedSubMenus] = useState({
    General: false,
    Booking: false,
    AdminPanel: false
  });
  const setCaretIcon = (ExpandableId) => {
    return expandedSubMenus[ExpandableId] ? "caret_down" : "caret_up";
  };
  const handleExpandableStatusChanged = (event) => {
    if (isMini) {
      setIsMini(!isMini);
    }
    const targetId = event.currentTarget.id;
    setExpandedSubMenus((prevState) => ({
      ...prevState,
      [targetId]: !isMini ? event.detail : !prevState[targetId]
    }));
  };

  const drawerSideBar = () => {
    return (
      <OwcList id={generateID.UUID(Module.SIDEPANEL.name, `drawersidebar`, "list")}>
        {sideMenu.map((item, i) => {
          if (item.children?.length === 0 && !item.hidden) {
            return (
              <OwcListItem
                selected={item.isActive}
                onClick={() => handleRedirect(item)}
                noBorder
                key={`${snakeCase(item.text)}_${i}`}
                id={generateID.UUID(Module.SIDEPANEL.name, `${snakeCase(item.text)}`, "li")}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {typeof item.icon === "string" ? (
                    <OwcIcon
                      name={item.icon}
                      type="legacy"
                      className={styles.appdrawerIconColor}
                      id={generateID.UUID(Module.SIDEPANEL.name, `${snakeCase(item.text)}_icon`, "li")}
                    />
                  ) : (
                    <div
                      className={styles.appdrawerIconStyle}
                      id={generateID.UUID(Module.SIDEPANEL.name, `${snakeCase(item.text)}_icon`, "li")}
                    >
                      {item.icon}
                    </div>
                  )}
                  <span
                    style={{ marginLeft: THEME["one-spacer-16"] }}
                    id={generateID.UUID(Module.SIDEPANEL.name, `${snakeCase(item.text)}_text`, "span")}
                  >
                    {item.text}
                  </span>
                </div>
              </OwcListItem>
            );
          } else {
            return (
              <>
                <OwcExpandable
                  id={item.text}
                  style={{ width: "100%" }}
                  variant="dense"
                  onStatusChanged={(ev) => handleExpandableStatusChanged(ev)}
                  expanded={expandedSubMenus[item.text]}
                >
                  <span
                    slot="control"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      height: THEME["one-sizer-base-100"],
                      padding: "0 1rem"
                    }}
                  >
                    {typeof item.icon === "string" ? (
                      <OwcIcon
                        name={item.icon}
                        type="legacy"
                        id={generateID.UUID(Module.SIDEPANEL.name, `drawersidebar`, "icon_legacy")}
                        className={styles.appdrawerIconColor}
                      />
                    ) : (
                      <div
                        className={styles.appdrawerIconStyle}
                        id={generateID.UUID(Module.SIDEPANEL.name, `drawersidebar`, "item_icon")}
                      >
                        {item.icon}
                      </div>
                    )}
                    <OwcTypography
                      variant="subtitle1"
                      style={{ marginLeft: THEME["one-spacer-16"], color: THEME["one-color-cobas-gray-700"] }}
                      id={generateID.UUID(Module.SIDEPANEL.name, `drawersidebar`, "text_item")}
                    >
                      {item.text}
                    </OwcTypography>
                    <OwcIconButton
                      icon={setCaretIcon(item.text)}
                      flat
                      style={{ margin: "0 8px 0 auto", pointerEvents: "none" }}
                    />
                  </span>
                  <span
                    id={generateID.UUID(Module.SIDEPANEL.name, `drawersidebar`, "list_span")}
                    slot="content"
                    style={{ padding: "0" }}
                  >
                    {!isMini &&
                      item.children &&
                      item.children.map((subItem, i) => {
                        return (
                          <OwcListItem
                            key={`${snakeCase(subItem.text)}_${i}`}
                            iconType="outlined"
                            icon={subItem.icon}
                            selected={subItem.isActive}
                            onClick={() => handleRedirect(subItem)}
                            noBorder
                            size="compact"
                            indent="2"
                            disabled={subItem.isDisabled === true}
                            id={generateID.UUID(Module.SIDEPANEL.name, `drawersidebar`, "list_subItem")}
                          >
                            {subItem.text}
                          </OwcListItem>
                        );
                      })}
                  </span>
                </OwcExpandable>
              </>
            );
          }
        })}
      </OwcList>
    );
  };

  return (
    <div
      className={styles.appdrawerOwcdrawer}
      style={{ height: containerHeight }}
      id={generateID.UUID(Module.SIDEPANEL.name, `AppDrawer`, "main")}
    >
      <OwcDrawer id="drawer" mode="push" hideBackdrop visible={sideMenu.length !== 0} mini={isMini}>
        <div
          slot="panel"
          className={styles.appdrawerPanel}
          id={generateID.UUID(Module.SIDEPANEL.name, `AppDrawer`, "panel_sidebar")}
        >
          {drawerSideBar()}
        </div>
        <div
          slot="header"
          className={styles.appdrawerHeader}
          id={generateID.UUID(Module.SIDEPANEL.name, `AppDrawer`, "slot_header")}
        >
          <OwcIconButton
            flat
            id="menu-button"
            icon="menu"
            style={{ margin: "8px" }}
            onClick={() => (!isMini ? setIsMini(true) : setIsMini(false))}
          />

          <OwcIconButton
            flat
            id="collapse-button"
            icon="clear"
            className={styles.appdrawerMenuclearicon}
            onClick={() => (!isMini ? setIsMini(true) : setIsMini(false))}
          />
        </div>

        <div
          className={styles.appdrawerDrawercontent}
          id={generateID.UUID(Module.SIDEPANEL.name, `AppDrawer`, "content")}
        >
          <main id={generateID.UUID(Module.SIDEPANEL.name, `AppDrawer`, "main_child")} style={{ padding: "1rem" }}>
            {children}
          </main>
        </div>
      </OwcDrawer>
    </div>
  );
};
const mapStateToProps = (store) => {
  return {
    selectedType: store.user.selected.type,
    applicationSets: store.user.applicationSets,
    email: store.user.email,
    NotificationBanner: store.user.NotificationBanner
  };
};
export default compose(connect(mapStateToProps), withApollo)(AppDrawer);
